import React, {useRef} from 'react';
import {MapEditor} from "components/MapEditor";
import {Layer, Source} from "react-map-gl/mapbox";
import {Trail, TrailFormData} from "types/Trail";
import {TrailWaypointFormData} from "types/TrailWaypoint";
import {feature, featureCollection} from "services/geometry";
import {Feature, FeatureCollection, Geometry, Point, Polygon} from "geojson";
import {FieldArray, FieldArrayRenderProps, useFormikContext} from "formik";
import TrailType from "types/TrailType";
import centroid from "@turf/centroid";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {restrictToVerticalAxis, restrictToWindowEdges} from "@dnd-kit/modifiers";
import {SortableContext, verticalListSortingStrategy} from "@dnd-kit/sortable";
import WaypointListItem from "./WaypointListItem";
import useGetOffers from "../../../hooks/api/useGetOffers";

// Types ----

type WaypointMapperProps = {
  name: string
  trail?: Trail | null
  flyToGeometry?: Geometry
}

// Helpers ----

function waypointPositionFeatures(waypoints: Array<TrailWaypointFormData>): FeatureCollection {
  return featureCollection(waypoints
    .filter(waypoint => !!waypoint.position)
    .map((waypoint, index) => feature(
      index,
      {
        type: 'point',
        waypointId: waypoint.id,
        name: waypoint.name,
        number: index + 1,
      },
      waypoint.position as Point
    ))
  );
}

function waypointAreaFeatures(waypoints: Array<TrailWaypointFormData>): FeatureCollection {
  return featureCollection(waypoints
    .filter(waypoint => !!waypoint.area)
    .map((waypoint, index) => feature(
      index,
      {
        type: 'area',
        waypointId: waypoint.id,
      },
      waypoint.area as Polygon
    ))
  );
}

// Component ----

function WaypointMapper({name, trail, flyToGeometry}: WaypointMapperProps) {
  const form = useFormikContext<TrailFormData>();

  // Keep a reference to the waypoint field array helpers
  const waypointHelpers = useRef<FieldArrayRenderProps | null>(null);

  // Extract the list of waypoints
  const waypoints: Array<TrailWaypointFormData> = waypointHelpers.current?.form.values.waypoints ?? form.values.waypoints ?? [];

  // Extract the areas and positions to display on the map
  const waypointAreas = waypointAreaFeatures(waypoints);
  const waypointPositions = waypointPositionFeatures(waypoints);
  
  // Prepare sensors for the sortable list
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
  );

  // Fetch exhibits and facilities for selection
  const {data: offers} = useGetOffers({page: 1, perPage: 500});

  // Handle waypoint dragging
  function handleDragEnd(event: DragEndEvent) {
    const {active, over} = event;

    if(!over || !waypointHelpers.current) return;

    const activeIndex = waypoints.findIndex(waypoint => waypoint.id === active.id)
    const overIndex = waypoints.findIndex(waypoint => waypoint.id === over.id)

    if(activeIndex < 0 || overIndex < 0) return;

    if(activeIndex !== overIndex) {
      waypointHelpers.current.move(activeIndex, overIndex);

      waypointHelpers.current.form.values.waypoints.forEach((_waypoint: TrailWaypointFormData, index: number) => {
        waypointHelpers.current?.form.setFieldValue(`${name}.${index}.order`, index);
      })
    }
  }

  // Add a new waypoint when a new area is drawn
  function addWaypoints(features: Array<Feature>) {
    if(!waypointHelpers.current) return;

    const trailType = waypointHelpers.current?.form.values.type;
    const waypointCount = Number(waypointHelpers.current?.form.values.waypoints.length);

    features.forEach(feature => {
      // Only polygons related directly to waypoints
      if (feature.geometry.type !== 'Polygon') return;

      // Add to form
      waypointHelpers.current?.push({
        id: String(feature.id),
        name: '',
        description: '',
        elevation: 0,
        distance: 0,
        par: trailType === TrailType.GOLF ? 3 : undefined,
        area: feature.geometry,
        position: centroid(feature.geometry).geometry,
        order: waypointCount,
        exhibits: [],
        facilities: [],
      });
    });
  }

  // Update an existing waypoint if the area is move or redrawn
  function updateWaypoints(features: Array<Feature>) {
    if (!waypointHelpers.current) return;

    const waypoints: Array<TrailWaypointFormData> = waypointHelpers.current?.form.values.waypoints ?? [];

    features.forEach(feature => {
      // Only polygons related directly to waypoints
      if (feature.geometry.type !== 'Polygon') return;

      // Find the affected waypoint
      const waypointIndex = waypoints.findIndex(waypoint => waypoint.id === feature.properties?.waypointId);

      // Skip if the waypoint index could not be found
      if(waypointIndex < 0) return;

      // Update the waypoint area and position
      waypointHelpers.current?.form.setFieldValue(`${name}.${waypointIndex}`, {
        ...waypoints[waypointIndex],
        area: feature.geometry,
        position: centroid(feature.geometry).geometry,
      })
    });
  }

  // Remove an existing waypoint if the area is deleted
  function removeWaypoints(features: Array<Feature>) {
    if (!waypointHelpers.current) return;

    const waypoints: Array<TrailWaypointFormData> = waypointHelpers.current?.form.values.waypoints ?? [];

    features.forEach(feature => {
      // Only polygons related directly to waypoints
      if (feature.geometry.type !== 'Polygon') return;

      // Find the affected waypoint
      const waypointIndex = waypoints.findIndex(waypoint => waypoint.id === feature.properties?.waypointId);

      // Skip if the waypoint index could not be found
      if(waypointIndex < 0) return;

      // Remove the waypoint
      waypointHelpers.current?.remove(waypointIndex);

      // Reset the orderings
      waypointHelpers.current?.form.values.waypoints.forEach((_waypoint: TrailWaypointFormData, index: number) => {
        waypointHelpers.current?.form.setFieldValue(`${name}.${index}.order`, index);
      })
    });
  }

  return (
    <>
      <MapEditor
        height="32rem"
        tools={{polygon: true, trash: true}}
        flyToGeometry={flyToGeometry}
        features={waypointAreas}
        onFeaturesCreated={addWaypoints}
        onFeaturesUpdated={updateWaypoints}
        onFeaturesDeleted={removeWaypoints}
      >
        <Source type="geojson" id="waypoint-positions" data={waypointPositions}>
          <Layer
            id="ngage-waypoint-number"
            type="symbol"
            layout={{'text-field': ['get', 'number'], 'text-anchor': 'center'}}
            paint={{'text-halo-color': '#ececec', 'text-halo-width': 2}}
          />
        </Source>
      </MapEditor>

      <FieldArray name={name}>
        {(helpers) => {

          // Keep a reference to the field array helpers
          waypointHelpers.current = helpers;

          // Render the sortable list
          return (
            <div className="space-y-4">
              <DndContext
                sensors={sensors}
                modifiers={[restrictToWindowEdges, restrictToVerticalAxis]}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={waypoints}
                  strategy={verticalListSortingStrategy}
                >
                  {waypoints.map((waypoint, index) => (
                    <WaypointListItem
                      name={name}
                      key={waypoint.id}
                      id={waypoint.id}
                      trailType={helpers.form.values.type}
                      trailIsCircuit={helpers.form.values.is_circuit}
                      waypoint={waypoint}
                      offers={offers?.data ?? []}
                      media={trail?.waypoints[index]?.media ?? []}
                      advisories={trail?.waypoints[index]?.advisories ?? []}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )
        }}
      </FieldArray>
    </>
  );
}

export default WaypointMapper;